/**
 * useFeatureGate — Lese-Hook für das BISRemote-Lizenz-Modell.
 *
 * Holt /billing/overview einmal pro Sitzung und stellt drei Helper zur Verfügung:
 *   - hasFeature(name): prüft ob ein bestimmtes Feature freigeschaltet ist
 *   - plan: aktuelles Plan-Kürzel (free|professional|business|…)
 *   - addons: Liste aktiver AddOn-Typen ("bisremote", "bisremote_accounting", …)
 *
 * Backend-Quelle:  api/src/Service/FeatureGateService.php (hasFeature + ADDON_FEATURES)
 * Vor allem für:   FeatureGate.tsx, UpgradeBanner.tsx
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../api/client';

type FeatureKey =
  | 'bisremote_cloud'
  | 'bisremote_accounting'
  | 'reports_basic'
  | 'reports_advanced'
  | 'accounting_export'
  | 'multi_tenant'
  | 'advanced_inventory'
  | 'b2b_portal'
  | 'bisrepricer'
  | 'dms_revisionssicher'
  | string;

interface LicenseSummary {
  plan: string | null;
  plan_name: string | null;
  features: Record<string, boolean>;
  addons: string[];
  subscription_status: string | null;
  is_active: boolean;
}

interface BillingOverviewResponse {
  success?: boolean;
  data?: { license: LicenseSummary };
  license?: LicenseSummary;
}

interface FeatureGateContextValue {
  loading: boolean;
  error: string | null;
  plan: string | null;
  planName: string | null;
  addons: string[];
  isActive: boolean;
  hasFeature: (name: FeatureKey) => boolean;
  /** Returns the AddOn key the user has to buy to unlock this feature, if any. */
  requiredAddonFor: (name: FeatureKey) => string | null;
  refresh: () => Promise<void>;
}

const FeatureGateContext = createContext<FeatureGateContextValue | null>(null);

// Map feature key → Produkt/AddOn das den Zugang freischaltet.
// Zwei eigenständige Pakete seit 28.05.2026, beide standalone buchbar im License-Shop:
//  - bisremote (10 €): Remote-Steuerung (Settings/Push/mehrere Kassen)
//  - bis-insights (15 €, ehemals 'bisremote-accounting'): Detail-Reports/BI/Buchhaltung
//    (WaWi-Statistiken, POS-Kassenjournal, UStVA/EÜR/BWA/DATEV/DSFinV-K, Cloud-Historie)
const FEATURE_TO_ADDON: Record<string, string> = {
  bisremote_cloud: 'bisremote',
  bisinsights: 'bis-insights',
  bisstatistics: 'bis-insights',
  bisremote_accounting: 'bis-insights', // Legacy-Alias
  wawi_reports: 'bis-insights',
  wawi_statistics: 'bis-insights',
  reports_basic: 'bis-insights',
  reports_advanced: 'bis-insights',
  accounting_export: 'bis-insights',
  advanced_inventory: 'inventory',
  b2b_portal: 'b2b_portal',
  bisrepricer: 'bisrepricer',
  multi_tenant: 'multi_tenant',
  // BIS Archiv (revisionssicheres DMS) — standalone buchbares Addon 'bis-archiv'
  dms_revisionssicher: 'bis-archiv',
  // Online-Tischreservierung (Gastro-Add-on, öffentlicher Buchungsweg) — 'online-tischreservierung'
  online_reservation: 'online-tischreservierung',
};

export function FeatureGateProvider({ children }: { children: React.ReactNode }) {
  const [summary, setSummary] = useState<LicenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Token-Auflösung ROUTE-ABHÄNGIG: Im Kundencenter (MyPortal, /my/*) liegt der JWT unter
      // 'my_portal_auth', in der Haupt-App unter 'auth_token' (api-Client). Früher wurde IMMER
      // 'my_portal_auth' zuerst genommen — ein VERALTETER MyPortal-Token (aus einer alten
      // Kundencenter-Session, den der Haupt-Login nicht abräumt) ließ dann in der Haupt-App
      // billing/overview auf 401 laufen; der globale fetch-Interceptor (main.tsx) wertete das
      // als Logout → Login-Schleife trotz frischem auth_token. Daher pro Kontext den passenden
      // Token bevorzugen (mit Fallback auf den jeweils anderen, falls nur einer existiert).
      const readPortalToken = (): string | null => {
        try { return JSON.parse(localStorage.getItem('my_portal_auth') || 'null')?.token ?? null; } catch { return null; }
      };
      const inMyPortal = typeof window !== 'undefined' && window.location.pathname.startsWith('/my');
      const token = inMyPortal
        ? (readPortalToken() ?? localStorage.getItem('auth_token'))
        : (localStorage.getItem('auth_token') ?? readPortalToken());
      const res = await fetch(`${API_URL}/billing/overview`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`billing/overview HTTP ${res.status}`);
      }
      const response = (await res.json()) as BillingOverviewResponse;
      const license = response?.data?.license ?? response?.license ?? null;
      if (!license) {
        throw new Error('Keine Lizenz-Daten im Billing-Overview gefunden');
      }
      setSummary(license);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      setError(message);
      // Auf Fehler: kein Feature freischalten — UI zeigt Upgrade-Banner statt Crash
      setSummary({
        plan: null,
        plan_name: null,
        features: {},
        addons: [],
        subscription_status: null,
        is_active: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  const hasFeature = useCallback(
    (name: FeatureKey) => Boolean(summary?.features?.[name]),
    [summary]
  );

  const requiredAddonFor = useCallback(
    (name: FeatureKey) => FEATURE_TO_ADDON[name] ?? null,
    []
  );

  const value = useMemo<FeatureGateContextValue>(
    () => ({
      loading,
      error,
      plan: summary?.plan ?? null,
      planName: summary?.plan_name ?? null,
      addons: summary?.addons ?? [],
      isActive: summary?.is_active ?? false,
      hasFeature,
      requiredAddonFor,
      refresh: fetchSummary,
    }),
    [loading, error, summary, hasFeature, requiredAddonFor, fetchSummary]
  );

  return <FeatureGateContext.Provider value={value}>{children}</FeatureGateContext.Provider>;
}

export function useFeatureGate(): FeatureGateContextValue {
  const ctx = useContext(FeatureGateContext);
  if (!ctx) {
    throw new Error('useFeatureGate muss innerhalb von <FeatureGateProvider> aufgerufen werden');
  }
  return ctx;
}
